// src/hooks/useImageWithAuth.js
import { useState, useEffect, useRef } from 'react';
import { getMediaUrl } from '../utils/mediaUtils';

const useImageWithAuth = (appName, imagePath) => {
  const [image, setImage] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const loadedUrls = useRef(new Set());
  const failedUrls = useRef(new Set()); // Track failed URLs to prevent retry loops
  const lastImagePath = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    // If imagePath is null/undefined, reset state
    if (!imagePath) {
      if (lastImagePath.current !== null) {
        setImage(null);
        setStatus('idle');
        setError(null);
        lastImagePath.current = null;
      }
      return;
    }

    // Check if this URL has permanently failed
    if (failedUrls.current.has(imagePath)) {
      if (status !== 'failed') {
        setStatus('failed');
        setError('Access denied or image not available');
      }
      return;
    }

    // If imagePath changed, reset and load new image
    if (lastImagePath.current !== imagePath) {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setImage(null);
      setStatus('loading');
      setError(null);
      lastImagePath.current = imagePath;
    }

    // Skip if we've already successfully loaded this URL
    if (loadedUrls.current.has(imagePath)) {
      if (status !== 'loaded') {
        setStatus('loaded');
      }
      return;
    }

    // Don't retry if already loading or failed
    if (status === 'loading' || status === 'failed') {
      return;
    }

    let isMounted = isMountedRef.current;
    let objectUrl = null;

    const loadImage = async () => {
      try {
        if (!isMounted) return;

        console.log(`Loading image: ${imagePath}`);
        setStatus('loading');

        const mediaUrl = getMediaUrl(appName, imagePath);
        if (!mediaUrl) {
          throw new Error('Invalid media URL');
        }

        // Create abort controller for this request
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        // Fetch image with authentication
        const token = localStorage.getItem('authToken');
        const headers = token ? { 'Authorization': `Token ${token}` } : {};

        const response = await fetch(mediaUrl, {
          credentials: 'include',
          headers: headers,
          signal: signal
        });

        if (!isMounted) return;

        if (response.status === 403 || response.status === 401) {
          // Permission denied - don't retry
          console.warn(`Access denied for image: ${imagePath} (${response.status})`);
          failedUrls.current.add(imagePath);
          setError('Access denied. You don\'t have permission to view this image.');
          setStatus('failed');
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        const img = new Image();

        img.onload = () => {
          if (!isMounted) return;
          console.log(`Image loaded successfully: ${imagePath}`);
          loadedUrls.current.add(imagePath);
          setImage(img);
          setStatus('loaded');
          setError(null);
        };

        img.onerror = () => {
          if (!isMounted) return;
          console.error(`Image failed to load: ${imagePath}`);
          failedUrls.current.add(imagePath);
          setError('Failed to load image');
          setStatus('failed');
          if (objectUrl) URL.revokeObjectURL(objectUrl);
        };

        img.src = objectUrl;

      } catch (err) {
        if (!isMounted) return;

        // Don't treat abort as an error
        if (err.name === 'AbortError') {
          console.log(`Image loading aborted: ${imagePath}`);
          return;
        }

        console.error(`Error loading image ${imagePath}:`, err);
        failedUrls.current.add(imagePath);
        setError(err.message);
        setStatus('failed');
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      } finally {
        abortControllerRef.current = null;
      }
    };

    loadImage();

    // Cleanup function
    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [appName, imagePath]); // Remove 'status' from dependencies to prevent loops

  return [image, status, error];
};

export default useImageWithAuth;